import {
  OnInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/input';
import { Language } from './types/language';
import { LanguagesEnum } from './enums/languages.enum';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatOption, MatSelect } from '@angular/material/select';
import { filter, Subscription } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { AppRoutesEnum } from './enums/app-routes.enum';
import { AuthService } from './services/auth-service';
import { CommunicationService } from './services/communication-service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatFormField,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    TranslatePipe,
    MatIcon,
    MatButton,
  ],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './app.scss',
})
export class App implements OnInit, OnDestroy {
  protected readonly auth = inject(AuthService);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  readonly communicationService = inject(CommunicationService);

  user = this.auth.user;

  protected maxMode = signal<boolean>(false);

  protected availableLanguages: Language[] = [
    {
      label: 'DE',
      value: LanguagesEnum.de,
    },
    {
      label: 'FR',
      value: LanguagesEnum.fr,
    },
    {
      label: 'IT',
      value: LanguagesEnum.it,
    },
  ];

  protected languageFormControl = new FormControl(LanguagesEnum.de);

  private subscription = new Subscription();

  protected userMenuOpened = signal(false);

  private appNameMapping: Record<AppRoutesEnum, string> = {
    [AppRoutesEnum.anlagePlanung]: 'Anlageplanung',
    [AppRoutesEnum.belvoto]: 'Belvoto',
    [AppRoutesEnum.callback]: 'Callback',
    [AppRoutesEnum.cockpit]: 'Cockpit',
    [AppRoutesEnum.ninHistory]: 'NIN History',
  };

  protected activeAppName = signal('');

  private languageKey = 'language';

  constructor() {
    this.translateService.addLangs(['de', 'en', 'fr', 'it']);
  }

  ngOnInit() {
    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(this.onNavigationChange.bind(this)),
    );
    this.subscription.add(
      this.languageFormControl.valueChanges.subscribe(this.onLanguageChange.bind(this)),
    );
    const savedLanguage = localStorage.getItem(this.languageKey);
    if (savedLanguage) {
      this.translateService.use(savedLanguage);
      this.languageFormControl.setValue(this.getLanguageEnumByName(savedLanguage), {
        emitEvent: false,
      });
    }
    this.subscription.add(
      this.communicationService.maximizedChange.subscribe(this.onMaximizeChange.bind(this)),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private getLanguageEnumByName(language: string) {
    let output = LanguagesEnum.de;
    for (const key in LanguagesEnum) {
      if ((LanguagesEnum as any)[key] === language) {
        output = (LanguagesEnum as any)[key];
        break;
      }
    }

    return output;
  }

  private onLanguageChange(language: LanguagesEnum | null) {
    if (language) {
      this.translateService.use(language);
      localStorage.setItem(this.languageKey, language);
    }
  }

  protected logoutUser() {
    localStorage.removeItem(this.languageKey);
    this.auth.logout();
  }

  private onMaximizeChange(maximized: boolean) {
    this.maxMode.set(maximized);
  }

  private onNavigationChange(event: NavigationEnd) {
    const url: string = event.url.replace('/', '');
    this.activeAppName.set((this.appNameMapping as any)[url] ?? '');
  }

  protected onUserMenuOpen() {
    this.userMenuOpened.set(true);
  }

  protected onUserMenuClose() {
    this.userMenuOpened.set(false);
  }

  protected redirectToAuthentication() {
    this.auth.redirectToAuthentication('/');
  }
}
